import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, Clock, FileSpreadsheet, Download, Eye, RefreshCw, Database, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const cr = (v) => (Number(v || 0) / 1e7).toFixed(2);

function LastCard({ label, log, receivedToday }) {
  const ok = !!log;
  return (
    <Card className="p-4 flex-1 min-w-[260px]" data-testid={`so-monitor-last-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</p>
        {ok ? (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${receivedToday ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            {receivedToday ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />} {receivedToday ? "Received today" : "Not yet today"}
          </span>
        ) : <span className="text-[11px] text-slate-400">No upload yet</span>}
      </div>
      {ok ? (
        <>
          <p className="text-2xl font-bold text-slate-900 font-mono">{cr(log.total_amount)} <span className="text-xs font-normal text-slate-400">INR Cr</span></p>
          <p className="text-xs text-slate-500 mt-1">{log.rows?.toLocaleString("en-IN")} rows · {fmtAt(log.at)} · {log.filename}</p>
          {log.months?.length > 0 && <p className="text-[11px] text-slate-400 mt-0.5">Months {log.months[0]} → {log.months[log.months.length - 1]}{log.skipped ? ` · ${log.skipped} rows skipped` : ""}</p>}
        </>
      ) : <p className="text-sm text-slate-400">Waiting for Milestone's first file.</p>}
    </Card>
  );
}

function PreviewDialog({ log, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["so-preview", log?.id], enabled: !!log,
    queryFn: async () => (await api.get(`/so-data/monitor/preview/${log.id}`, { params: { limit: 100 } })).data,
  });
  return (
    <Dialog open={!!log} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl" data-testid="so-monitor-preview">
        <DialogHeader><DialogTitle className="font-heading text-base flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-brand" /> {log?.filename} <span className="text-xs text-slate-400 font-normal">first 100 of {data?.total_rows ?? "…"} rows</span></DialogTitle></DialogHeader>
        <div className="max-h-[65vh] overflow-auto border border-slate-200 rounded-md">
          {isLoading ? <p className="p-6 text-sm text-slate-400 text-center">Loading…</p> : (
            <table className="text-xs whitespace-nowrap">
              <thead className="bg-slate-50 sticky top-0"><tr>{(data?.header || []).map((h, i) => <th key={i} className="px-2 py-1.5 text-left font-semibold text-slate-600 border-b">{String(h)}</th>)}</tr></thead>
              <tbody>{(data?.rows || []).map((r, i) => <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">{r.map((c, j) => <td key={j} className="px-2 py-1 text-slate-700">{String(c)}</td>)}</tr>)}</tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExportCard({ fys = [], months = [] }) {
  const [kind, setKind] = useState("both");
  const [fy, setFy] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get("/so-data/export", { params: { kind, fy, month_from: from, month_to: to }, responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url;
      a.download = `milestone_${kind}_${fy || "all"}.xlsx`; a.click(); URL.revokeObjectURL(url);
      toast.success("Milestone data downloaded");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const sel = "h-9 rounded-md border border-input bg-white px-2 text-sm";
  const [reBusy, setReBusy] = useState(false);
  const reparse = async () => {
    setReBusy(true);
    try {
      const { data } = await api.post("/so-data/reparse-latest", {}, { timeout: 180000 });
      const b = data.booking, i = data.billing;
      toast.success(`Re-imported: booking ${b.ok ? `${b.rows} rows (${b.with_zone} with Zone)` : b.error}; billing ${i.ok ? `${i.rows} rows (${i.with_zone} with Zone)` : i.error}`, { duration: 8000 });
    } catch (e) { toast.error(apiError(e)); } finally { setReBusy(false); }
  };
  return (
    <Card className="p-4 mb-4" data-testid="so-export-card">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><FileSpreadsheet className="h-3.5 w-3.5" /> Download Milestone data</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-500 space-y-1"><span>Dataset</span><br />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={sel} data-testid="so-export-kind"><option value="both">Booking + Billing</option><option value="booking">Order Booking (SO)</option><option value="billing">Billing (Invoices)</option></select></label>
        <label className="text-xs text-slate-500 space-y-1"><span>Financial year</span><br />
          <select value={fy} onChange={(e) => setFy(e.target.value)} className={sel} data-testid="so-export-fy"><option value="">All</option>{fys.map((f) => <option key={f} value={f}>{f}</option>)}</select></label>
        <label className="text-xs text-slate-500 space-y-1"><span>From month</span><br />
          <select value={from} onChange={(e) => setFrom(e.target.value)} className={sel} data-testid="so-export-from"><option value="">Start</option>{months.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
        <label className="text-xs text-slate-500 space-y-1"><span>To month</span><br />
          <select value={to} onChange={(e) => setTo(e.target.value)} className={sel} data-testid="so-export-to"><option value="">End</option>{months.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
        <Button onClick={run} disabled={busy} className="h-9 gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="so-export-download">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download Excel</Button>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">One sheet per dataset with every SO/invoice line (client, branch, employee, product family, zone, vertical, product category, amounts) plus a Summary sheet with monthly totals in ₹ and Cr.</p>
      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={reparse} disabled={reBusy} data-testid="so-reparse-latest">{reBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Re-import latest files</Button>
        <p className="text-[11px] text-slate-400">Re-reads the last booking and billing files Milestone sent so newly added columns (Zone, Sales Branch, Vertical, Product Category) show on the Sales Dashboard without waiting for tomorrow's push.</p>
      </div>
    </Card>
  );
}

export default function SOUploadMonitor() {
  const [preview, setPreview] = useState(null);
  const { data, isLoading, refetch } = useQuery({ queryKey: ["so-monitor"], queryFn: async () => (await api.get("/so-data/monitor")).data, refetchInterval: 120000 });
  const download = async (log) => {
    try {
      const res = await api.get(`/so-data/monitor/file/${log.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = log.filename || "upload.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="so-upload-monitor">
      <PageHeader title="Milestone Upload Monitor" subtitle="Daily Order Booking & Billing files received from the Milestone ERP API — status, totals, failures and the original Excel files.">
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => refetch()} data-testid="so-monitor-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </PageHeader>
      {isLoading ? <p className="text-sm text-slate-400">Loading…</p> : (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            <LastCard label="Booking" log={data?.last_booking} receivedToday={data?.received_today?.booking} />
            <LastCard label="Billing" log={data?.last_billing} receivedToday={data?.received_today?.billing} />
            <Card className="p-4 min-w-[220px]" data-testid="so-monitor-health">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Health</p>
              <p className={`text-2xl font-bold font-mono ${data?.failures_7d ? "text-red-600" : "text-emerald-600"}`}>{data?.failures_7d ?? 0} <span className="text-xs font-normal text-slate-400">failures · 7 days</span></p>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Database className="h-3 w-3" /> {data?.rows?.bookings?.toLocaleString("en-IN")} booking rows · {data?.rows?.billings?.toLocaleString("en-IN")} billing rows in CRM</p>
            </Card>
          </div>
          <ExportCard fys={data?.fys} months={data?.months} />
          <Card className="overflow-hidden" data-testid="so-monitor-log">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2 font-semibold">Received</th><th className="px-3 py-2 font-semibold">Type</th><th className="px-3 py-2 font-semibold">File</th><th className="px-3 py-2 font-semibold text-right">Rows</th><th className="px-3 py-2 font-semibold text-right">Total (INR Cr)</th><th className="px-3 py-2 font-semibold">Result</th><th className="px-3 py-2 font-semibold text-right">Excel</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.logs || []).map((l) => (
                  <tr key={l.id} data-testid={`so-monitor-row-${l.id}`} className={l.kind === "failed" ? "bg-red-50/40" : ""}>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtAt(l.at)}</td>
                    <td className="px-3 py-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${l.kind === "booking" ? "bg-sky-50 text-sky-700" : l.kind === "billing" ? "bg-violet-50 text-violet-700" : "bg-red-50 text-red-700"}`}>{l.kind}</span></td>
                    <td className="px-3 py-2 text-slate-600 max-w-[260px] truncate" title={l.filename}>{l.filename}</td>
                    <td className="px-3 py-2 text-right font-mono">{l.rows ?? "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{l.kind === "failed" ? "—" : cr(l.total_amount)}</td>
                    <td className="px-3 py-2 text-xs max-w-[340px]">
                      {l.kind === "failed" ? <span className="text-red-700 flex items-start gap-1" title={l.error}><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span className="truncate">{l.error}</span></span>
                        : <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Imported{l.amount_header ? ` · ${l.amount_header}` : ""}{l.by ? ` · ${l.by}` : ""}</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {l.storage_path ? (
                        <>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-600" onClick={() => setPreview(l)} title="View rows" data-testid={`so-monitor-view-${l.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-600" onClick={() => download(l)} title="Download Excel" data-testid={`so-monitor-download-${l.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        </>
                      ) : <span className="text-[11px] text-slate-300">not archived</span>}
                    </td>
                  </tr>
                ))}
                {(data?.logs || []).length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-slate-400">No uploads received yet.</td></tr>}
              </tbody>
            </table>
          </Card>
        </>
      )}
      <PreviewDialog log={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
